import { Component, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { ApiService } from 'src/app/services/api/api.service';
import { FundService } from 'src/app/services/fund/fund.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import Utils from 'src/app/helpers/utils';
import {
  BreadcrumbService,
  BreadcrumbItem,
} from 'src/app/services/breadcrumb/breadcrumb.service';

@Component({
  selector: 'app-dashboard-fund-approval',
  templateUrl: './dashboard-fund-approval.component.html',
  styleUrls: ['./dashboard-fund-approval.component.scss'],
})
export class DashboardFundApprovalComponent implements OnInit {
  reproveForm = this.formBuilder.group({
    description: [null, [Validators.required]],
  });

  tabs: Tab[] = [
    { label: 'Dados básicos', value: 'basic_data' },
    { label: 'Transferência', value: 'transfer' },
    { label: 'Dados complementares', value: 'complementary' },
    { label: 'Cotas', value: 'quote' },
    { label: 'Taxas', value: 'tax' },
    { label: 'Bancário', value: 'bank' },
    // { label: 'Anexos', value: 'files' },
    { label: 'Minutas', value: 'draft' },
    { label: 'Cedentes', value: 'assignors' },
    { label: 'Cotistas', value: 'shareholder' },
    { label: 'Prestadores de serviço', value: 'provider' },
  ];

  permissionObj: PermissionValues = {
    canChange: false,
    canApprove: false,
    canReject: false,
    canReturn: false,
  };

  tabIndex = 0;
  activedTab = 'basic_data';

  fund: any = undefined;
  approval: UserApprovalResponse = undefined;

  self_view = false;

  signing = false;

  approve = false;
  warningModal = false;
  resetApproval = false;
  loading = true;

  constructor(
    private route: ActivatedRoute,
    private toast: ToastService,
    private api: ApiService,
    protected sanitizer: DomSanitizer,
    public fundService: FundService,
    public router: Router,
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private storageService: StorageService,
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnDestroy(): void {
    this.fundService.reset();
  }

  ngOnInit(): void {
    this.authService.canViewPage(['de_can_view_fund']);

    this.tabs.filter((tab) =>
      this.authService.verifyPermission(`de_can_view_fund_${tab.value}`)
    );

    this.route.params.subscribe((params) => {
      this.loading = true;
      this.tabIndex = history.state.step ?? 0;
      this.activedTab = this.tabs[this.tabIndex].value;
      this.getData();
      this.setPermissions();
      this.setBreadcrumb();
    });

    this.tabIndex = history.state.step ?? 0;
    this.activedTab = this.tabs[this.tabIndex].value;
    this.getData();
    this.setPermissions();
    this.setBreadcrumb();
  }

  async getFund() {
    try {
      const data = await this.api.get({
        route: 'api/v2/approvals/version/',
        params: {
          fund: this.route.snapshot.params.id,
          //   tab: '',
        },
        token: true,
      });

      const fund = { ...data };

      this.approval = fund[0];
      this.fund = fund[0].fund[0];
      
      const clientObj = Array.isArray(fund[0].client) ? fund[0].client[0] : (fund[0].client || {});

      //   this.fundService.signatures = fund[0].signatures;
      this.fundService.fundData = {
        ...this.fundService.fundData,
        basic: {
          ...this.fund,
          fund_current_profile: this.fund.fund_current_profile || clientObj.fund_current_profile
        },
      };
      this.setBreadcrumb(); // Atualizar breadcrumb após carregar os dados
    } catch (error) {
      this.toast.show(
        'error',
        'Erro',
        'Erro ao carregar dados básicos: ' + error
      );
    }
  }

  getChoicesName(object: string, value: any) {
    const choices = this.fundService.choices;

    const choiceObject = choices[object];

    if (choiceObject) {
      return choiceObject.find((_choice: any) => _choice.value == value);
    }

    return { label: '', value: '' };
  }

  async getChoices() {
    try {
      const res = await this.api.get({
        route: 'api/registration/new/fund-choices',
        token: true,
      });

      const choices: { [Key in any]: any } = {};

      Object.entries(res).forEach(([key, value]: any) => {
        choices[key.toLowerCase()] = value.map((item: any) => {
          return {
            label: item.label,
            value: String(item.id),
          };
        });
      });

      const filterFunctions = [4, 6, 7, 8, 9, 10];

      choices.service_provider_choices =
        choices.service_provider_choices.filter((service_provider: any) =>
          filterFunctions.includes(Number(service_provider.value))
        );

      this.fundService.choices = choices;
    } catch (error) {
      this.toast.show('error', 'Erro!', 'Erro ao carregar dados');
    }
  }

  async signApproval(approve: boolean) {
    this.signing = true;
    try {
      const payload = {
        user_approval: this.approval.id,
        department_approval: this.approval.current_department.id,
        approved: approve,
        restart: this.resetApproval,
        reason: approve ? '' : this.reproveForm.value.description,
        signed: true,
      };

      const res = await this.api.post({
        route: 'api/approvals/user_approval_signature/',
        token: true,
        body: payload,
      });

      if (approve) {
        this.toast.show(
          'info',
          'Tudo certo',
          'O fundo foi aprovado com sucesso'
        );
      } else {
        this.toast.show(
          'info',
          `Fundo ${this.resetApproval ? 'retornado' : 'reprovado'}`,
          'Uma notificação foi enviada ao responsável'
        );
      }

      this.warningModal = false;
      this.reproveForm.reset();

      if (this.reproveForm.valid || approve) {
        this.warningModal = false;
        this.signing = false;

        if (this.resetApproval) {
          this.router.navigateByUrl('/app/dashboard');
        } else {
          this.loading = true;
          await this.getData();
        }
      }
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao aprovar o fundo, tente novamente mais tarde'
      );
      this.signing = false;
    }
  }

  async getData() {
    await Promise.all([this.getFund(), this.getChoices(), this.getUserArea()]);
    this.loading = false;
  }

  onChangeTab(index: number) {
    this.activedTab = this.tabs[index].value;
    this.tabIndex = index;
  }

  async rediretToEdit() {
    this.router.navigate([`app/funds/edit/${this.fund.id}/`]);
  }

  closeWarningModal() {
    this.warningModal = false;
    this.reproveForm.reset();
  }

  handleApproval(approve: boolean, reset?: boolean) {
    this.approve = approve;
    this.warningModal = true;
    this.resetApproval = reset ?? false;
  }

  showCtaOptions: boolean = false;

  toggleShowOptions() {
    if (this.isApproveDisabled()) {
      return;
    }
    this.showCtaOptions = !this.showCtaOptions;
  }

  userArea: string = null;

  getInitials(name: any) {
    return Utils.getInitials(name);
  }

  async getUserArea(): Promise<void> {
    try {
      const { data } = await this.api.get<{
        data: {
          area: string;
        };
      }>({
        route: 'api/approvals/v3/user_approval/my-area-dashboard/summary/',
        token: true,
      });

      this.userArea = data?.area || null;
    } catch (error) {
      console.error('[DashboardFundApproval] getUserArea - ERRO:', error);
      this.userArea = null;
    }
  }

  isMasterUser(): boolean {
    const user = this.authService.user || this.authService.getUser();
    if (!user) {
      return false;
    }
    return (
      user.type_permission === 'master' ||
      user.type_permission === 'owner' ||
      user.is_approver === true
    );
  }

  isInApproval(): boolean {
    return this.approval && !this.approval.completed;
  }

  isUserInCurrentDepartment(): boolean {
    if (!this.userArea) {
      return false;
    }

    if (!this.approval || !this.approval.current_department) {
      return false;
    }

    const currentDept = this.approval.current_department;

    // Ajuste para lidar com a estrutura vinda da API v2/version que tem .name direto
    // The user provided JSON shows current_department having .name directly
    const deptName = currentDept.department?.name || (currentDept as any).name;

    if (!deptName || typeof deptName !== 'string' || deptName.trim() === '') {
      return false;
    }

    const normalizeString = (str: string): string => {
      return str?.toLowerCase()?.trim()?.replace(/\s+/g, ' ') || '';
    };

    const userAreaNormalized = normalizeString(this.userArea);
    const deptNameNormalized = normalizeString(deptName);

    if (!userAreaNormalized || !deptNameNormalized) {
      return false;
    }

    return userAreaNormalized === deptNameNormalized;
  }

  isApproveDisabled(): boolean {
    if (!this.approval) {
      return true;
    }

    if (this.approval.completed) {
        return true;
    }

    if (!this.userArea) {
      return true;
    }

    if (this.userArea === 'Todos (Usuário Master)') {
      return false;
    }

    if (this.isUserInCurrentDepartment()) {
      return false;
    }

    return true;
  }

  setPermissions() {
    const permissions = Object.keys(this.permissionObj).map(
      (key) => `de_can_${key.split('can').join('').toLowerCase()}_fund`
    );

    // Verificar se é owner ou master
    const permissionType = this.storageService.getPermissionType();
    const isOwnerOrMaster =
      permissionType === 'owner' || permissionType === 'master';
    const isSuperuser = this.authService.user?.is_superuser || false;

    permissions.forEach((permission, idx) => {
      const key = Object.keys(this.permissionObj)[idx];
      const hasPermission = this.authService.verifyPermission(permission);

      // Se for owner, master ou superuser, permitir aprovação mesmo sem a permissão específica
      if (key === 'canApprove' && (isOwnerOrMaster || isSuperuser)) {
        this.permissionObj[key] = true;
      } else {
        this.permissionObj[key] = hasPermission;
      }
    });
  }

  setBreadcrumb(): void {
    const canViewFunds = this.authService.verifyPermission('de_can_view_fund');
    const fundId = this.route.snapshot.params.id;
    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: 'Fundos',
        url: '/app/funds',
        clickable: canViewFunds,
      },
      {
        label: 'Detalhes do Fundo',
        url: `/app/funds/approval/${fundId}`,
        clickable: false, // Página atual, não precisa ser clicável
      },
    ];
    this.breadcrumbService.setBreadcrumbs(breadcrumbs);
  }
}
